import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import BuyerRequestCard from './BuyerRequestCard';
import { Button } from '@/components/ui/button';
import { PlusCircle, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from "@/components/ui/use-toast";
import { getSupabaseClient, isSupabaseInitialized, waitForInitialization } from "@/integrations/supabase/client";
import { getSafeClient } from "@/integrations/supabase/safeClient";

interface BuyerRequest {
  id: string;
  niche: string;
  drMin: number;
  daMin: number;
  budgetMin: number;
  budgetMax: number;
  linkType: string;
  notes: string;
  expiresAt: string;
  createdAt: string;
  userId: string;
  userName?: string | null;
  userAvatar?: string | null;
}

const fetchRecentRequests = async (): Promise<BuyerRequest[]> => {
  try {
    console.log("[RecentRequests] Starting fetch...");
    
    // Wait for Supabase client to be initialized
    console.log("[RecentRequests] Waiting for Supabase client initialization...");
    await waitForInitialization();
    console.log("[RecentRequests] Supabase client initialized successfully");
    
    // Get supabase client
    const supabaseClient = await getSafeClient();
    console.log("[RecentRequests] Got Supabase client");
    
    // First, get the buyer requests
    console.log("[RecentRequests] Fetching buyer requests...");
    const { data: requestsData, error: requestsError } = await supabaseClient
      .from('buyer_requests')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(6);
    
    if (requestsError) {
      console.error('[RecentRequests] Error fetching buyer requests:', requestsError);
      console.error('[RecentRequests] Error details:', JSON.stringify(requestsError));
      toast({
        title: "Data Error",
        description: "Failed to load recent requests: " + requestsError.message,
        variant: "destructive"
      });
      return [];
    }
    
    if (!requestsData || requestsData.length === 0) {
      console.log("No buyer requests found in the database");
      return [];
    }
    
    console.log(`Found ${requestsData.length} buyer requests`);
    
    // Map requests to our interface format
    const formattedRequests: BuyerRequest[] = requestsData.map(item => ({
      id: item.id,
      niche: item.niche,
      drMin: item.dr_min || 0,
      daMin: item.da_min || 0,
      budgetMin: item.budget_min_cents ? item.budget_min_cents / 100 : 0,
      budgetMax: item.budget_max_cents ? item.budget_max_cents / 100 : 0,
      linkType: item.link_type || '',
      notes: item.notes || '',
      expiresAt: item.expires_at,
      createdAt: item.created_at,
      userId: item.user_id,
      userName: "Loading...",
      userAvatar: null
    }));
    
    // Get unique user IDs to fetch profiles
    const userIds = [...new Set(requestsData.map(item => item.user_id))];
    
    // If there are user IDs, fetch their profiles
    if (userIds.length > 0) {
      try {
        const { data: profilesData, error: profilesError } = await supabaseClient
          .from('profiles')
          .select('id, name, avatar_url')
          .in('id', userIds);
        
        if (profilesError) {
          console.error('Error fetching profiles:', profilesError);
        } else if (profilesData) {
          // Create a lookup map for quick access
          const profilesMap = profilesData.reduce((acc, profile) => {
            acc[profile.id] = profile;
            return acc;
          }, {} as Record<string, any>);
          
          // Update the requests with profile data
          formattedRequests.forEach(request => {
            const profile = profilesMap[request.userId];
            if (profile) {
              request.userName = profile.name || "Anonymous";
              request.userAvatar = profile.avatar_url;
            } else {
              request.userName = "Anonymous";
            }
          });
        }
      } catch (profileError) {
        console.error('Error processing profiles:', profileError);
      }
    }
    
    return formattedRequests;
  } catch (error) {
    console.error('Unhandled error in fetchRecentRequests:', error);
    console.error('Error stack:', error instanceof Error ? error.stack : "No stack available");
    throw error;
  }
};

const RecentRequests = () => {
  const navigate = useNavigate();
  const { data: requests, isLoading, error, refetch } = useQuery({
    queryKey: ['recentBuyerRequests'],
    queryFn: fetchRecentRequests,
    retry: 5,
    retryDelay: attempt => Math.min(1000 * 2 ** attempt, 30000),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
  });

  // Add a retry button for manual refetching
  const handleRetry = () => {
    toast({
      title: "Retrying",
      description: "Attempting to fetch buyer requests again...",
    });
    refetch();
  };

  const handlePostRequest = () => {
    navigate('/create-request');
  };

  const handleViewAll = () => {
    navigate('/buyer-requests');
  };

  return (
    <section className="bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-8">
          <div>
            <h2 className="text-2xl font-bold mb-2">Recent Buyer Requests</h2>
            <p className="text-gray-600 text-sm sm:text-base">
              See what potential customers are looking for
            </p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button variant="outline" onClick={handleViewAll} className="flex-1 sm:flex-initial">
              View All
            </Button>
            <Button className="bg-brand-purple hover:bg-brand-purple/90 flex-1 sm:flex-initial" onClick={handlePostRequest}>
              <PlusCircle className="h-4 w-4 mr-2" />
              <span className="truncate">Post a Request</span>
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="h-10 w-10 text-brand-purple animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-2">Unable to load buyer requests.</p>
            <p className="text-sm text-gray-500 mb-4">
              {error instanceof Error ? error.message : "Connection error"}
            </p>
            <Button variant="outline" className="mt-4" onClick={handleRetry}>
              Try Again
            </Button>
          </div>
        ) : (
          <div>
            {requests && requests.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-6 gap-3 overflow-hidden">
                {requests.map((request) => (
                  <BuyerRequestCard key={request.id} {...request} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-white rounded-lg shadow-sm border border-gray-100">
                <p className="text-gray-600 mb-4">No buyer requests found.</p>
                <p className="text-sm text-gray-500 mb-6">
                  This could be because:
                </p>
                <ul className="list-disc text-left max-w-md mx-auto mb-6 text-sm text-gray-500">
                  <li className="mb-1">There are no buyer requests in the database yet</li>
                  <li className="mb-1">Supabase connection is still initializing</li>
                  <li className="mb-1">Your Row Level Security policies might be restricting access</li>
                </ul>
                <div className="flex flex-wrap justify-center gap-3">
                  <Button variant="outline" className="mr-2" onClick={handleRetry}>
                    Retry
                  </Button>
                  <Button onClick={handlePostRequest}>
                    Create Your First Request
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default RecentRequests;
